import { Injectable } from "@angular/core";
import {
    AbstractControl,
    AbstractControlOptions,
    AsyncValidatorFn,
    UntypedFormArray,
    UntypedFormControl,
    UntypedFormGroup,
    ValidatorFn,
    Validators
} from "@angular/forms";

import {
    controlsToArray,
    DffFormControlDropdownListOptions,
    DffFormControlOptions,
    DffFormControlType,
    DffOprindeligAktuelVaerdiFormGroup,
    DffValidators,
    DffReactiveFormControlValueMapper as Map
} from "@dffedb/forms";
import { addYears, DffOprindeligAktuelVaerdier, EntitetStatus } from "@dffedb/util";
import { NoteBruger, NoteFormBuilder, NoteHenvendelsestype, NoteOmraade, NoteOpgavestyringStatus } from "@e-forsyning/common/note";

import { KomponentBruger } from "./model/komponent-bruger.model";
import { KomponentDimension } from "./model/komponent-dimension.model";
import { KomponentGruppe } from "./model/komponent-gruppe.model";
import { KomponentTypeDimension } from "./model/komponent-type-dimension.model";
import { KomponentType } from "./model/komponent-type.model";
import { Komponent } from "./model/komponent.model";

@Injectable({ providedIn: "root" })
export class KomponentFormBuilder {
    public komponentGruppeToFormGroup(
        komponentGruppe: KomponentGruppe,
        komponentTyper: KomponentType[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        return new UntypedFormGroup({
            id: new UntypedFormControl(komponentGruppe.id),
            komponenter: this.komponenterToFormArray(
                komponentGruppe.komponenter,
                komponentTyper,
                noteOmraader,
                noteHenvendelsestyper,
                noteOpgavestyringStatusser,
                noteBrugere
            ),
            navn: new UntypedFormControl(komponentGruppe.navn)
        });
    }

    public komponenterToFormArray(
        komponenter: Komponent[],
        komponentTyper: KomponentType[],
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormArray {
        const result = komponenter.map((komponent) => {
            const komponentType = komponentTyper.find((t) => t.id === komponent.komponentTypeId);
            return this.komponentToFormGroup(
                komponent,
                komponentType,
                noteOmraader,
                noteHenvendelsestyper,
                noteOpgavestyringStatusser,
                noteBrugere
            );
        });
        return new UntypedFormArray(result);
    }

    public komponentToFormGroup(
        komponent: Komponent,
        komponentType: KomponentType,
        noteOmraader: NoteOmraade[],
        noteHenvendelsestyper: NoteHenvendelsestype[],
        noteOpgavestyringStatusser: NoteOpgavestyringStatus[],
        noteBrugere: NoteBruger[]
    ): UntypedFormGroup {
        const skjulServiceDatoer = komponentType.serviceinterval <= 0;
        const result = new UntypedFormGroup({
            bruger: this.brugerToFormGroup(komponent.bruger),
            dimensioner: this.dimensionerToFormArray(komponent.dimensioner, komponentType.dimensioner),
            fabrikatNavn: new UntypedFormControl(komponentType.fabrikat.navn),
            id: new UntypedFormControl(komponent.id),
            komponentTypeId: new UntypedFormControl(komponent.komponentTypeId),
            nedtagetDato: this.opretOprindeligAktuelFormGroup(komponent.nedtagetDato, {
                controlType: DffFormControlType.DateInput,
                placeholder: "Dato for nedtagelse"
            }),
            noter: NoteFormBuilder.noterToFormArray(
                komponent.noter,
                noteOmraader,
                noteHenvendelsestyper,
                noteOpgavestyringStatusser,
                noteBrugere
            ),
            opsatDato: this.opretOprindeligAktuelFormGroup(komponent.opsatDato, {
                controlType: DffFormControlType.DateInput,
                placeholder: "Dato"
            }),
            senestServiceDato: this.opretOprindeligAktuelFormGroup(komponent.senestServiceDato, {
                controlType: DffFormControlType.DateInput,
                placeholder: "Seneste servicedato",
                hidden: skjulServiceDatoer
            }),
            serieNr: this.opretOprindeligAktuelFormGroup(komponent.serieNr, {
                controlType: DffFormControlType.TextInput,
                placeholder: "Serienummer"
            }),
            serviceDato: this.opretOprindeligAktuelFormGroup(komponent.serviceDato, {
                controlType: DffFormControlType.DateInput,
                placeholder: "Næste servicedato",
                hidden: skjulServiceDatoer
            }),
            serviceIntervalAar: this.opretOprindeligAktuelFormGroup(
                { oprindelig: komponentType.serviceinterval, aktuel: komponentType.serviceinterval },
                {
                    controlType: DffFormControlType.NumberInput,
                    placeholder: "Serviceinterval",
                    suffix: "år",
                    disabled: true,
                    hidden: skjulServiceDatoer
                }
            ),
            status: new UntypedFormControl(komponent.status),
            tekst: this.opretOprindeligAktuelFormGroup(komponent.tekst, { placeholder: "Tekst" })
        });

        // Opdater status, hvis der sker ændringer på komponenten.
        this.updateStatus(result, komponentType);
        result.valueChanges.subscribe(() => this.updateStatus(result, komponentType));

        result.controls["senestServiceDato"].valueChanges.subscribe(() => this.senestServiceDatoChanged(result, komponentType));

        return result;
    }

    public brugerToFormGroup(bruger: KomponentBruger): UntypedFormGroup {
        return new UntypedFormGroup({
            initialer: new UntypedFormControl(bruger.initialer),
            navn: new UntypedFormControl(bruger.navn)
        });
    }

    public dimensionerToFormArray(dimensioner: KomponentDimension[], dimensionTyper: KomponentTypeDimension[]): UntypedFormArray {
        return new UntypedFormArray(
            dimensionTyper.map((dimensionType) => {
                const dimension = dimensioner.find((d) => d.komponentTypeDimensionId === dimensionType.id);
                return this.dimensionToFormGroup(dimension, dimensionType);
            })
        );
    }

    public dimensionToFormGroup(dimension: KomponentDimension, dimensionType: KomponentTypeDimension): UntypedFormGroup {
        return new UntypedFormGroup({
            id: new UntypedFormControl((dimension && dimension.id) || 0),
            komponentTypeDimensionId: new UntypedFormControl(dimensionType.id),
            vaerdi: this.dimensionToFormControl(dimension, dimensionType)
        });
    }

    public dimensionToFormControl(dimension: KomponentDimension, dimensionType: KomponentTypeDimension): UntypedFormGroup {
        const result = this.opretOprindeligAktuelFormGroup(
            (dimension && dimension.vaerdi) || { oprindelig: dimensionType.standardvaerdi, aktuel: dimensionType.standardvaerdi },
            {
                controlType: this.getFormControlType(dimensionType.datatype),
                dropdownListOptions: this.getOptions(dimensionType),
                placeholder: dimensionType.navn,
                suffix: dimensionType.enhed
            },
            Validators.maxLength(30)
        );
        return result;
    }

    public opretOprindeligAktuelFormGroup(
        value: DffOprindeligAktuelVaerdier,
        options: DffFormControlOptions = {},
        validatorFn?: ValidatorFn | ValidatorFn[] | AbstractControlOptions | null,
        asyncValidator?: AsyncValidatorFn | AsyncValidatorFn[] | null
    ): UntypedFormGroup {
        const result = new DffOprindeligAktuelVaerdiFormGroup(value, options, validatorFn, asyncValidator);
        return result;
    }

    public getFormControlType(datatype: number): DffFormControlType {
        switch (datatype) {
            case 20:
                return DffFormControlType.NumberInput;
            case 30:
            case 40:
                return DffFormControlType.DropdownList;
            default:
                return DffFormControlType.TextInput;
        }
    }

    public getOptions(dimensionType: KomponentTypeDimension): DffFormControlDropdownListOptions {
        if (dimensionType.datatype === 30) {
            return [
                { key: "true", value: "Ja" },
                { key: "false", value: "Nej" }
            ];
        }
        return [{}, ...dimensionType.valgmuligheder.map((value) => ({ key: value, value }))];
    }

    public formGroupToKomponentGruppe(formGroup: UntypedFormGroup): KomponentGruppe {
        const result: KomponentGruppe = {
            id: Map.toInteger(formGroup.controls["id"]),
            komponenter: this.formArrayToKomponenter(formGroup.controls["komponenter"] as UntypedFormArray),
            navn: Map.toString(formGroup.controls["navn"])
        };
        return result;
    }

    public formArrayToKomponenter(formArray: UntypedFormArray): Komponent[] {
        const result = formArray.controls.map((c) => this.formGroupToKomponent(c as UntypedFormGroup));
        return result;
    }

    public formGroupToKomponent(formGroup: UntypedFormGroup): Komponent {
        const result: Komponent = {
            bruger: this.formGroupToBruger(formGroup.controls["bruger"] as UntypedFormGroup),
            dimensioner: this.formArrayToDimensioner(formGroup.controls["dimensioner"] as UntypedFormArray),
            id: Map.toInteger(formGroup.controls["id"]),
            komponentTypeId: Map.toInteger(formGroup.controls["komponentTypeId"]),
            nedtagetDato: Map.toOprindeligAktuelDates(formGroup.controls["nedtagetDato"]),
            noter: NoteFormBuilder.formArrayToNoter(formGroup.controls["noter"] as UntypedFormArray),
            opsatDato: Map.toOprindeligAktuelDates(formGroup.controls["opsatDato"]),
            senestServiceDato: Map.toOprindeligAktuelDates(formGroup.controls["senestServiceDato"]),
            serieNr: Map.toOprindeligAktuelStrings(formGroup.controls["serieNr"]),
            serviceDato: Map.toOprindeligAktuelDates(formGroup.controls["serviceDato"]),
            status: Map.toInteger(formGroup.controls["status"]),
            tekst: Map.toOprindeligAktuelStrings(formGroup.controls["tekst"])
        };
        return result;
    }

    public formGroupToBruger(formGroup: UntypedFormGroup): KomponentBruger {
        const result: KomponentBruger = {
            initialer: Map.toString(formGroup.controls["initialer"]),
            navn: Map.toString(formGroup.controls["navn"])
        };
        return result;
    }

    public formArrayToDimensioner(formArray: UntypedFormArray): KomponentDimension[] {
        const result = formArray.controls.map((c) => this.formGroupToDimension(c as UntypedFormGroup));
        return result;
    }

    public formGroupToDimension(formGroup: UntypedFormGroup): KomponentDimension {
        const result: KomponentDimension = {
            id: Map.toInteger(formGroup.controls["id"]),
            komponentTypeDimensionId: Map.toInteger(formGroup.controls["komponentTypeDimensionId"]),
            vaerdi: Map.toOprindeligAktuelStrings(formGroup.controls["vaerdi"])
        };
        return result;
    }

    private senestServiceDatoChanged(komponentFormGroup: UntypedFormGroup, komponentType: KomponentType): void {
        // Vi får aktiveret dette event både når vi ændrer 'senestServiceDato' og når vi trykker på 'Udfør opgave'.
        // Vi ønsker kun at opdatere 'naesteServiceDato', hvis vi rent faktisk har foretaget en ændring i 'senestServiceDato'.
        const changes = this.getChanges(komponentFormGroup, "senestServiceDato.aktuel");
        if (changes.currentValue === changes.previousValue) {
            return;
        }

        // Vi ønsker kun at opdatere 'naesteServiceDato', hvis vi har et 'serviceinterval' som kan bruges til beregningen.
        if (komponentType.serviceinterval <= 0) {
            return;
        }

        // Hvis der er angivet 'serviceinterval' på komponenttypen, skal næste 'serviceDato' automatisk beregnes og opdateres.
        const senesteServiceDatoValue = komponentFormGroup.get("senestServiceDato.aktuel").value;
        const naesteServiceDatoValue = senesteServiceDatoValue && addYears(senesteServiceDatoValue, komponentType.serviceinterval);
        const naesteServiceDato = komponentFormGroup.get("serviceDato.aktuel");
        naesteServiceDato.setValue(naesteServiceDatoValue);
    }

    // Utility-rutine, som i et valueChanges-event kan fortælle hvad værdien var før ændringen og hvad den er nu.
    private getChanges(formGroup: UntypedFormGroup, path: string): { previousValue: any; currentValue: any } {
        const propertyNames = path.split(".");
        const previousValue = propertyNames.reduce((obj, propertyName) => obj[propertyName], formGroup.value);
        const currentValue = formGroup.get(path).value;
        return { previousValue, currentValue };
    }

    private updateStatus(formGroup: UntypedFormGroup, komponentType: KomponentType): void {
        const nyStatus = this.beregnStatus(formGroup);
        const status = formGroup.get("status") as UntypedFormControl;
        if (status.value !== nyStatus) {
            status.setValue(nyStatus);
        }

        if (nyStatus === EntitetStatus.Uaendret) {
            status.markAsPristine();
        } else {
            status.markAsDirty();
        }

        this.updateValidators(formGroup, komponentType);
    }

    private updateValidators(formGroup: UntypedFormGroup, komponentType: KomponentType): void {
        const minDateValidator = DffValidators.minDate(new Date(1900, 0, 1)); // 01-01-1900
        const maxDateValidator = DffValidators.maxDate(new Date(2200, 0, 1)); // 01-01-2200
        const status = this.beregnStatus(formGroup);
        const slettet = status === EntitetStatus.Slettet;
        const skjulServiceDatoer = komponentType.serviceinterval <= 0;

        const opsatDato = formGroup.get("opsatDato.aktuel");
        const senestServiceDato = formGroup.get("senestServiceDato.aktuel");
        const serieNr = formGroup.get("serieNr.aktuel");
        const serviceDato = formGroup.get("serviceDato.aktuel");
        const tekst = formGroup.get("tekst.aktuel");

        const opsatDatoValidators = slettet ? [] : [minDateValidator, maxDateValidator];
        const senestServiceDatoValidators = slettet
            ? []
            : skjulServiceDatoer
            ? []
            : [Validators.required, minDateValidator, maxDateValidator];
        const serieNrValidators = slettet
            ? []
            : komponentType.serieNrKraevet
            ? [Validators.required, Validators.maxLength(12)]
            : [Validators.maxLength(12)];
        const serviceDatoValidators = slettet ? [] : skjulServiceDatoer ? [] : [Validators.required, minDateValidator, maxDateValidator];
        const tekstValidators = slettet ? [] : [Validators.maxLength(30)];

        this.setValidators(opsatDato, opsatDatoValidators);
        this.setValidators(senestServiceDato, senestServiceDatoValidators);
        this.setValidators(serieNr, serieNrValidators);
        this.setValidators(serviceDato, serviceDatoValidators);
        this.setValidators(tekst, tekstValidators);
    }

    private setValidators(
        control: AbstractControl,
        validators: ((control: AbstractControl) => import("@angular/forms").ValidationErrors)[]
    ): void {
        // Når man ændrer validatorer, bør man efterfølgende kalde updateValueAndValidity.
        // Ellers risikerer vi at få fejlen ExpressionChangedAfterItHasBeenCheckedError.
        control.setValidators(validators);
        control.updateValueAndValidity({ emitEvent: false });
    }

    private beregnStatus(formGroup: UntypedFormGroup): EntitetStatus {
        // Status er Tilføjet eller Slettet: Så er komponenten ændret
        const status = formGroup.get("status");
        if (status.value === EntitetStatus.Tilfoejet || status.value === EntitetStatus.Slettet) {
            return status.value;
        }

        // Er der ændrede noter? Så er komponenten også ændret
        const noter = formGroup.get("noter") as UntypedFormArray;
        if (noter.controls.some((n) => n.get("status").value !== EntitetStatus.Uaendret)) {
            return EntitetStatus.Aendret;
        }

        // Er der ændrede dimensioner? Så er komponenten også ændret
        const dimensioner = formGroup.get("dimensioner") as UntypedFormArray;
        if (dimensioner.controls.some((d) => d.get("vaerdi.aktuel").value !== d.get("vaerdi.oprindelig").value)) {
            return EntitetStatus.Aendret;
        }

        // Er der ændrede properties? Så er komponenten ændret
        const controls = controlsToArray(formGroup.controls).filter((c) => c instanceof DffOprindeligAktuelVaerdiFormGroup);
        if (controls.some((c) => c.dirty)) {
            return EntitetStatus.Aendret;
        }

        return EntitetStatus.Uaendret;
    }
}
